import styled from 'styled-components';

/* Visual tokens mirror the Finances pages (FakturowniaInvoices) so the command
 * center feels native: text #0f172a, secondary #64748b, accent #2563eb,
 * border #e2e8f0. Duplication is cheap and stable (per the Finances note). */

export const Wrapper = styled.div`
  width: 100%;
  margin: 32px 0 64px;
  color: #0f172a;
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 4px;
  flex-wrap: wrap;
`;

export const PageTitle = styled.h1`
  font-size: 20px;
  margin: 0;
  font-weight: 650;
  letter-spacing: -0.01em;
`;

export const RefreshButton = styled.button`
  margin-left: auto;
  border: 1px solid #2563eb;
  background: #fff;
  color: #2563eb;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #eff6ff;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

export const SubTitle = styled.div`
  color: #64748b;
  font-size: 13px;
  margin-bottom: 16px;
`;

export const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  margin-bottom: 16px;
`;

export const SearchInput = styled.input`
  flex: 1;
  min-width: 200px;
  box-sizing: border-box;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 10px;
  font: inherit;
  font-size: 13px;
  color: #0f172a;
`;

export const Toggle = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #64748b;
  cursor: pointer;
  white-space: nowrap;
`;

export const SectionTitle = styled.h2`
  font-size: 14px;
  font-weight: 650;
  margin: 28px 0 10px;
  color: #0f172a;
`;

export const Card = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  padding: 12px 16px;
`;

export const TabToolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

export const Table = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 13px;

  /* No text-align here — alignment is per-column via S.Th (the nested
   * selector would out-specify the Th class and pin every header left). */
  thead th {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #64748b;
    font-weight: 700;
    padding: 8px 12px;
    border-bottom: 1px solid #e2e8f0;
  }

  tbody td {
    padding: 10px 12px;
    border-bottom: 1px solid #e2e8f0;
    vertical-align: middle;
  }

  tbody tr {
    cursor: pointer;
  }

  tbody tr:hover {
    background: #f8fafc;
  }
`;

export const Th = styled.th<{ align?: 'left' | 'right' }>`
  text-align: ${({ align }) => (align === 'right' ? 'right' : 'left')};
  cursor: pointer;
  user-select: none;
  white-space: nowrap;

  &:hover {
    color: #0f172a;
  }
`;

export const SortArrow = styled.span`
  color: #2563eb;
  margin-left: 2px;
`;

export const GroupCell = styled.td`
  b {
    display: block;
    font-weight: 650;
    color: #0f172a;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  span {
    display: block;
    font-size: 12px;
    color: #64748b;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const Num = styled.td<{ danger?: boolean; warn?: boolean }>`
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-weight: ${({ danger, warn }) => (danger || warn ? 700 : 400)};
  color: ${({ danger, warn }) =>
    danger ? '#dc2626' : warn ? '#d97706' : '#0f172a'};
`;

export const ActionCell = styled.td`
  text-align: right;
  white-space: nowrap;
`;

export const LinkBtn = styled.button`
  border: 0;
  background: transparent;
  padding: 0 0 0 12px;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  color: #2563eb;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

export const Muted = styled.div`
  font-size: 13px;
  color: #64748b;
  padding: 16px 0;
`;

/* --- Shared inputs (comms toolbar + global filter bar) --- */

export const Input = styled.input`
  box-sizing: border-box;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 10px;
  font: inherit;
  font-size: 13px;
`;

export const Select = styled.select`
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 7px 8px;
  font: inherit;
  font-size: 13px;
  background: #fff;
`;

/* --- Comms toolbar --- */

export const TaskBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

export const BucketCount = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: #64748b;
  background: #f1f5f9;
  border-radius: 999px;
  padding: 1px 8px;
`;

export const Chip = styled.span<{ tone?: 'warn' | 'accent' }>`
  font-size: 11px;
  font-weight: 600;
  border-radius: 999px;
  padding: 1px 8px;
  color: ${({ tone }) =>
    tone === 'warn' ? '#92400e' : tone === 'accent' ? '#1e3a8a' : '#475569'};
  background: ${({ tone }) =>
    tone === 'warn' ? '#fef3c7' : tone === 'accent' ? '#dbeafe' : '#f1f5f9'};
`;

/* --- "Waiting for a reply" panel --- */

export const UnansweredList = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 420px; /* ~10 rows */
  overflow-y: auto;
`;

export const UnansweredRow = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  border: 0;
  background: transparent;
  border-bottom: 1px solid #e2e8f0;
  padding: 8px 4px;
  font: inherit;
  font-size: 13px;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: #f8fafc;
  }

  &:last-child {
    border-bottom: 0;
  }
`;

export const ChannelBadge = styled.span<{
  tone: 'email' | 'slack' | 'channel';
}>`
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  border-radius: 999px;
  padding: 1px 7px;
  color: ${({ tone }) =>
    tone === 'email' ? '#1e3a8a' : tone === 'slack' ? '#5b21b6' : '#475569'};
  background: ${({ tone }) =>
    tone === 'email' ? '#dbeafe' : tone === 'slack' ? '#ede9fe' : '#f1f5f9'};
`;

export const UnansweredMain = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;

  b {
    font-weight: 600;
    color: #0f172a;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  span {
    font-size: 11px;
    color: #64748b;
  }
`;

export const UnreadBubble = styled.span`
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 700;
  border-radius: 999px;
  padding: 1px 8px;
  white-space: nowrap;
  color: #fff;
  background: #2563eb;
`;

export const UnansweredTime = styled.span`
  flex-shrink: 0;
  font-size: 12px;
  color: #64748b;
  font-variant-numeric: tabular-nums;
`;

export const AllAnswered = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #047857;
  padding: 8px 0;
`;

/* --- Communications browser --- */

export const StudentPickerBox = styled.div`
  flex: 1;
  min-width: 200px;
  font-size: 13px;
`;

export const MoreButton = styled.button`
  margin-top: 10px;
  width: 100%;
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 8px;
  padding: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #2563eb;
  cursor: pointer;

  &:hover {
    background: #f8fafc;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;
